import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { getTablesByTechnicalColumn } from 'src/endpoints'

import { Button, LightTable, Loader, Modal } from '@/components'
import { useApi } from '@/endpoints/hooks'
import { StructureObjectDto } from '@/endpoints/models'
import { useAppContext } from '@/hooks'
import { useOpenTab } from '@/pages/User/pages/Home/components/Dependencies/hooks/useOpenTab'
import { LightTableColumn } from '@/types'

import { formatterCell1, formatterCell2 } from '../../../utils'
import { TechnicalImpactModalProps } from '../types'
import { useDownloadImpactCsv } from './hooks/useDownloadImpactCsv'

export const TechnicalImpactModal = ({
	open,
	onClose,
	technicalColumn,
}: TechnicalImpactModalProps) => {
	const { t } = useAppContext()
	const { openTabById } = useOpenTab()
	const navigate = useNavigate()

	const handleEdit = useCallback(
		(tableId?: number) => {
			if (tableId === undefined) {
				return
			}

			navigate('/')
			openTabById(tableId)
		},
		[openTabById],
	)

	const tables = useApi(getTablesByTechnicalColumn(technicalColumn.id || 0))

	const { isDownloading, onDownloadCsv } = useDownloadImpactCsv()

	const columnsLightTable = useMemo<LightTableColumn<StructureObjectDto>[]>(
		() => [
			{
				field: 'name',
				title: t('NAME'),
				width: 175,
				formatter: (cell) => formatterCell1(cell),
			},
			{
				field: 'code',
				title: t('CODE'),
				width: 175,
				formatter: (cell) => formatterCell1(cell),
			},
			{
				field: 'structureId',
				width: 30,
				maxWidth: 30,
				formatter: (cell) => formatterCell2(cell, handleEdit, t),
			},
		],
		[handleEdit, t],
	)

	const isSomethingImpacted = (tables.data?.length ?? 0) > 0

	return (
		<Modal
			open={open}
			onClose={onClose}
			header={t('IMPACTED_TABLES')}
			stickyFooter
			footer={
				<Button icon={faCheck} onClick={onClose}>
					{t('CLOSE')}
				</Button>
			}
			contentStyle={{
				width: 700,
				minWidth: 700,
			}}
			resizable
		>
			<>
				<Loader loaded={!tables.loading} $absolute />
				{!tables.loading && (
					<>
						<div style={{ marginBottom: '10px', display: 'flex' }}>
							{t('IMPACTED_TABLES')}: {tables.data?.length ?? ''}
							{isSomethingImpacted && (
								<Button
									onClick={() => onDownloadCsv(technicalColumn)}
									isLoading={isDownloading}
									disabled={isDownloading}
									style={{ marginLeft: 'auto' }}
								>
									{t('DOWNLOAD_IMPACT_AS_CSV')}
								</Button>
							)}
						</div>

						{isSomethingImpacted && (
							<LightTable<StructureObjectDto>
								data={tables.data || []}
								columns={columnsLightTable}
								keyField={'structureId'}
							/>
						)}
					</>
				)}
			</>
		</Modal>
	)
}
